import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import { addSvg } from "../shared/utils";
import { REMOVE_LAYER, TOGGLE_LAYER_VISIBILITY, UPDATE_LAYER_NAME, UPDATE_LAYER_OPACITY } from "../tools/actions/ActionHelper";
import RowHelper from "./edit/RowHelper";
import EventTypes from "../shared/EventTypes";

export default class LayerRow extends AbstractProvidedClass {
    editing = false;
    adjustingOpacity = false;

    constructor(provider, layer) {
        super(provider);

        this.layer = layer;

        this.row = this.p.createDiv()
            .parent(this.getTab('layers').rowContainer)
            .class('input-search-result')
            .mouseClicked(() => this.click())
            .mouseOver(() => this.mouseOver())
            .mouseOut(() => this.mouseOut());

        this.visibilityButton = this.p.createDiv()
            .parent(this.row)
            .class('btn-icon mar-l-xs align-center')
            .style('flex-shrink', '0')
            .mouseClicked(() => this.toggleVisibility());
        this.updateVisibilityIcon();

        this.label = this.p.createElement('label', this.layer.name)
            .class('input-search-result-name align-center')
            .parent(this.row);

        this.count = this.p.createElement('span', '0')
            .parent(this.row)
            .class('align-center')
            .style('opacity', '0.5')
            .style('margin-left', 'auto');

        this.input = this.p.createInput(this.layer.name)
            .parent(this.row)
            .class('input-text')
            .attribute('autocomplete', 'off')
            .style('flex', '1')
            .style('min-width', '0')
            .style('margin', '0 8px');
        this.input.elt.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') this.input.elt.blur();
        });
        this.input.elt.onfocus = () => this.enableHotKeys = false;
        this.input.elt.onblur = () => {
            this.enableHotKeys = true;
            this.finishEdit();
        };
        this.input.hide();

        this.opacitySlider = this.p.createElement('input')
            .parent(this.row)
            .attribute('type', 'range')
            .attribute('min', '0')
            .attribute('max', '1')
            .attribute('step', '0.01')
            .style('flex', '1')
            .style('min-width', '0')
            .style('margin', '0 8px');
        this.opacitySlider.elt.value = this.layer.opacity;
        this.opacitySlider.elt.oninput = () => {
            this.opacityAction?.update(parseFloat(this.opacitySlider.elt.value));
        };
        this.opacitySlider.elt.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') this.opacitySlider.elt.blur();
        });
        this.opacitySlider.elt.onblur = () => {
            this.finishOpacity();
        };
        this.opacitySlider.hide();

        this.helper = new RowHelper(provider, this);
        this.helper.createOptions(this.viewMode ? ['opacity'] : ['edit', 'opacity', 'active', 'remove']);

        if (this.touch) {
            this.moreButton = this.p.createDiv()
                .parent(this.row)
                .class('btn-icon')
                .mouseClicked(() => this.toggleRowOptions());
            addSvg(this.moreButton, 'more-2-fill');
            this.on(EventTypes.CLOSE_SIDEBAR, () => this.closeRowOptions());
        }

        this.on(EventTypes.TIMELINE_DATE_CHANGED, () => this.updateCount());

        this.layer.row = this;
    }

    click() {
        if (this.buttonClicked) return;
        this.tools.setTool('select');
        this.layer.select();
    }

    mouseOver() {
        if (this.editing || this.adjustingOpacity || this.touch) return;
        this.hoverSet.add(this.layer.getElements());
        this.label.hide();
        this.count.hide();
        this.options.show();
    }

    mouseOut() {
        if (this.editing || this.adjustingOpacity || this.touch) return;
        this.hoverSet.reset();
        this.label.show();
        this.count.show();
        this.options.hide();
    }

    toggleRowOptions() {
        if (this.options.elt.style.display === 'none') {
            this.openRowOptions();
        } else {
            this.closeRowOptions();
        }
    }

    openRowOptions() {
        this.label.hide();
        this.count.hide();
        this.moreButton?.hide();
        this.options.show();
        this._closeHandler = (e) => {
            if (!this.row.elt.contains(e.target)) {
                this.closeRowOptions();
            }
        };
        setTimeout(() => document.addEventListener('touchstart', this._closeHandler), 0);
    }

    closeRowOptions() {
        if (this._closeHandler) {
            document.removeEventListener('touchstart', this._closeHandler);
            this._closeHandler = null;
        }
        this.label.show();
        this.count.show();
        this.moreButton?.style('display', 'flex');
        this.options.hide();
    }

    toggleVisibility() {
        this.buttonClicked = true;
        setTimeout(() => this.buttonClicked = false, 100);
        this.actions.execute(TOGGLE_LAYER_VISIBILITY, this.layer);
        this.updateVisibilityIcon();
    }

    updateVisibilityIcon() {
        let icon = this.layer.visible ? 'eye-line' : 'eye-off-line';
        let tooltip = this.layer.visible ? 'Hide layer' : 'Show layer';
        this.visibilityButton.html('');
        this.visibilityButton.attribute('data-tooltip', tooltip);
        addSvg(this.visibilityButton, icon);
    }

    toggleEdit() {
        this.buttonClicked = true;
        setTimeout(() => this.buttonClicked = false, 100);
        if (this.editing) {
            this.finishEdit();
        } else {
            this.action ??= this.actions.new(UPDATE_LAYER_NAME, this.layer);
            this.editing = true;
            this.input.value(this.layer.name);
            this.input.show();
            this.input.elt.focus();
            this.input.elt.setSelectionRange(this.input.elt.value.length, this.input.elt.value.length);
            this.label.hide();
            this.options.hide();
            this.count.hide();
        }
    }

    finishEdit() {
        if (!this.editing) return;
        this.editing = false;
        let newName = this.input.value().trim();
        if (newName) {
            this.layer.name = newName;
        }
        this.input.hide();
        this.label.show();
        this.count.show();
        this.action?.finish();
        this.action = null;
    }

    adjustOpacity() {
        this.buttonClicked = true;
        setTimeout(() => this.buttonClicked = false, 100);
        this.adjustingOpacity = true;
        if (this.editMode) {
            this.opacityAction = this.actions.new(UPDATE_LAYER_OPACITY, this.layer);
        } else {
            this.opacityAction = {
                update: () => {
                    this.layer.opacity = this.opacitySlider.elt.value;
                },
                finish: () => {}
            };
        }
        this.label.hide();
        this.options.hide();
        this.opacitySlider.elt.value = this.layer.opacity;
        this.opacitySlider.show();
        this.opacitySlider.elt.focus();
    }

    finishOpacity() {
        this.adjustingOpacity = false;
        this.opacitySlider.hide();
        this.label.show();
        this.count.show();
        this.opacityAction?.finish();
        this.opacityAction = null;
    }

    updateOpacitySlider(value) {
        this.opacitySlider.elt.value = value;
    }

    updateCount() {
        let visible = this.layer.getElements().filter(el => el.currentlyVisible()).length;
        let total = this.layer.getElements().length;
        this.count.html(visible === total ? total : `${visible}/${total}`);
    }

    setActive() {
        this.buttonClicked = true;
        setTimeout(() => this.buttonClicked = false, 100);
        this.activeContextPanel.setActiveLayer(this.layer.id);
    }

    remove() {
        this.buttonClicked = true;
        setTimeout(() => this.buttonClicked = false, 100);
        this.actions.execute(REMOVE_LAYER, this.layer);
    }

    select() {
        this.row.style('background-color', '#aebbf240');
    }

    deselect() {
        this.row.style('background-color', null);
    }

    delete() {
        this.row.remove();
    }

    hide() {
        this.row.hide();
    }

    show() {
        this.row.style('display', 'flex');
    }
}
